import { useMutation, useQuery } from '@apollo/client/react';
import {
  STAFF_BILLING_STRIPE_COUPONS,
  STAFF_BILLING_UPSERT_STRIPE_COUPON,
  STAFF_BILLING_DELETE_STRIPE_COUPON,
} from '@staff-app/graphql-client';

/**
 * One row of the staff-owned catalog of Stripe coupons the Billing Console's Apply Coupon
 * card may attach to a customer.
 *
 * MAPPING + POLICY ONLY — deliberately no percent, no amount, no currency. What a coupon
 * takes off lives in Stripe, and a mirror here could only drift from the money a customer
 * is actually charged. `stripeCouponName` is a display cache of what Stripe returned when
 * the id was last round-tripped, the same role `stripeProductName` plays for a hardware
 * mapping.
 *
 * `requiresTaxExemptDoc` is the switch behind the whole document gate: true means apply
 * refuses unless a verified, non-archived `tax_exempt` certificate on the resolved account
 * is supplied. Only ROOT can set it, which is why this section is ROOT-only.
 */
export type StripeCouponCatalogEntry = {
  /** Primary key, slug-shaped (`^[A-Z0-9][A-Z0-9_-]{1,63}$`) — validated server-side. */
  couponKey: string;
  /** The Stripe coupon id. No prefix, unlike `prod_…` / `price_…`. */
  stripeCouponId: string;
  label: string;
  terms?: string | null;
  requiresTaxExemptDoc: boolean;
  /** Inactive rows keep the audit trail but never reach the Apply Coupon picker. */
  isActive: boolean;
  stripeCouponName?: string | null;
  /** When the id was last round-tripped against this environment's Stripe key. */
  stripeValidatedAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

/**
 * The maintenance list, INCLUDING inactive rows — deactivating is how a coupon is retired
 * without losing the row that explains a discount some customer is still carrying. The
 * Apply Coupon picker reads the separate active-only `staffBillingCouponCatalog`.
 */
export function useStripeCoupons() {
  return useQuery<{ staffBillingStripeCoupons: StripeCouponCatalogEntry[] }>(
    STAFF_BILLING_STRIPE_COUPONS,
    { fetchPolicy: 'cache-and-network' },
  );
}

/**
 * Both mutations refresh the Apply Coupon picker's catalog as well as this list, or the
 * Billing Console keeps offering the pre-edit catalog from cache — a deactivated coupon
 * would still be grantable in an open tab.
 */
const COUPON_REFETCH = {
  refetchQueries: ['staffBillingStripeCoupons', 'staffBillingCouponCatalog'],
};

/**
 * Save is FAIL-CLOSED on the server: it round-trips the id against this environment's
 * Stripe key before persisting, so a typo or a live-mode id pasted into sandbox is refused
 * at entry instead of being discovered mid-call with a customer. Errors are surfaced
 * verbatim — the mode hint in a 404 is the whole diagnosis.
 */
export function useUpsertStripeCoupon() {
  return useMutation<
    { staffBillingUpsertStripeCoupon: StripeCouponCatalogEntry },
    {
      couponKey: string;
      stripeCouponId: string;
      label: string;
      terms?: string;
      requiresTaxExemptDoc?: boolean;
      isActive?: boolean;
    }
  >(STAFF_BILLING_UPSERT_STRIPE_COUPON, COUPON_REFETCH);
}

/**
 * Deletes the CATALOG row only. Nothing in Stripe is touched and no customer already
 * carrying the discount loses it — this only stops the coupon being offered again.
 */
export function useDeleteStripeCoupon() {
  return useMutation<{ staffBillingDeleteStripeCoupon: boolean }, { couponKey: string }>(
    STAFF_BILLING_DELETE_STRIPE_COUPON,
    COUPON_REFETCH,
  );
}
